export type IndividualAiEstimatedCostGenerationStatus = 'NotStarted' | 'InProgress' | 'Completed' | 'Failed';
export type AggregateAiEstimatedCostGenerationStatus = 'NotStarted' | 'InProgress' | 'Completed' | 'Failed' | 'CompletedWithFailures' | 'InProgressWithFailures';

/**
 * One checked area of an area-checklist operation (Canopy Clearance /
 * Crown Reduction): the area name plus its per-area feet values.
 */
export interface OperationAreaEntryDto {
  area: string;
  [valueKey: string]: string | number;
}

export interface EstimateTreeOperationDto {
  id: number;
  treeId: number;
  operationType: TreeOperationType;
  estimatedCost?: number;
  aiEstimatedCost?: number;
  estimatedCostOverride?: number;
  // AC-267 B1: time is first-class — TOTAL labor minutes for the operation
  // (tree count folded in). Null = legacy row; the server back-fills minutes
  // whenever a cost is written.
  estimatedMinutes?: number;
  // SQ-520: the AI's suggested time and the actual time are stored and sent
  // in seconds; the *Minutes beside them are the nearest minute (kept for
  // older clients). Read the seconds; round only to show.
  aiEstimatedSeconds?: number;
  aiEstimatedMinutes?: number;
  actualSeconds?: number;
  actualMinutes?: number;
  /** Where the actual time came from: 'review' (moved by hand when the job was finished), 'derived', 'admin', … */
  actualsOrigin?: string | null;
  actualCost?: number;
  estimatedMinutesOverride?: number;
  aiEstimatedCostGenerationStatus?: IndividualAiEstimatedCostGenerationStatus;
  details: {[key: string]: string | number | OperationAreaEntryDto[]};
  /** SQ-487 §5c: the operation's own note. */
  notes?: string;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface InputEstimateTreeOperationDto {
  id?: number;
  treeId: number;
  operationType: TreeOperationType;
  estimatedCost?: number;
  details: {[key: string]: string | number | OperationAreaEntryDto[] | undefined};
  notes?: string | undefined;
}

export const TreeOperationType = {
  Thinning: 'Thinning',
  // Deadwood_Removal: 'Deadwood Removal',
  Custom_Trim_Prune: 'Custom Trim/Prune',
  Crown_Reduction: 'Crown Reduction',
  Tree_Removal: 'Tree Removal',
  Stump_Grinding: 'Stump Grinding',
  Cabling: 'Cabling',
  Debris_Management: 'Debris Management',
  Debris_Removal: 'Debris Removal',
  Ground_To_Canopy_Clearance: 'Ground to Canopy Clearance',
  Misc_Task: 'Misc Task',
  Tree_Health_Care: 'Tree Health Care',
  Whole_Stump_Removal: 'Whole Stump Removal'
} as const;
// eslint-disable-next-line @typescript-eslint/no-redeclare -- intentional const + companion type pattern
export type TreeOperationType = typeof TreeOperationType[keyof typeof TreeOperationType];

export interface EstimateTreeOperationBulkUpdateRequestEntryDto {
  treeId: number;
  operationId: number;
  estimatedCost?: number;
  estimatedCostOverride?: number;
  estimatedMinutes?: number;
  estimatedMinutesOverride?: number;
  details?: {[key: string]: string | number | undefined};
  notes?: string | undefined;
}

export interface EstimateTreeOperationBulkUpdateResponseEntryDto {
  treeId: number;
  operations: EstimateTreeOperationDto[];
}